/**
 * Icon Component
 *
 * Unified icon component using Lucide React icons.
 * Provides consistent sizing, colors, and accessibility across the app.
 */

import { forwardRef } from 'react';

import { cn } from '@/shared/utils';
import { IconKey, ICONS } from './icons';

interface IconProps extends Omit<React.SVGAttributes<SVGSVGElement>, 'className'> {
  icon: IconKey;
  size?: number;
  strokeWidth?: number;
  className?: string;
  'aria-label'?: string;
}

export const Icon = forwardRef<SVGSVGElement, IconProps>(function Icon(
  { icon, size = 24, strokeWidth = 2, className, 'aria-label': ariaLabel, ...rest },
  ref,
) {
  const LucideIcon = ICONS[icon];

  // Handle unknown icon keys gracefully (e.g., from database-stored category icons)
  if (!LucideIcon) {
    return null;
  }

  return (
    <LucideIcon
      ref={ref}
      size={size}
      strokeWidth={strokeWidth}
      className={cn(className)}
      aria-label={ariaLabel}
      aria-hidden={!ariaLabel}
      {...rest}
    />
  );
});

export default Icon;
export { IconKey, ICONS };
export type { IconProps };

